import flvjs from "flv.js";
import Hls from "hls.js";

const HLS_MIME_TYPES = [
  "application/x-mpegurl",
  "application/vnd.apple.mpegurl",
];

const FLV_MIME_TYPES = ["video/flv", "video/x-flv"];

const hlsInstances = new WeakMap();
const flvInstances = new WeakMap();

const VIDEO_SRC_ATTRS = ["src", "data-hls-src", "data-video-src", "data-src"];

export function isHlsUrl(url) {
  const path = (url.trim().toLowerCase().split(/[?#]/)[0] ?? "");
  return /\.m3u8?$/i.test(path);
}

export function isFlvUrl(url) {
  const path = (url.trim().toLowerCase().split(/[?#]/)[0] ?? "");
  return /\.flv$/i.test(path);
}

export function isHlsMimeType(type) {
  const normalized = type.trim().toLowerCase();
  return HLS_MIME_TYPES.some((mime) => normalized.includes(mime));
}

export function isFlvMimeType(type) {
  const normalized = type.trim().toLowerCase();
  return FLV_MIME_TYPES.some((mime) => normalized.includes(mime));
}

export function isLiveVideoSource(url, mimeType = "") {
  return (
    isHlsUrl(url) ||
    isHlsMimeType(mimeType) ||
    isFlvUrl(url) ||
    isFlvMimeType(mimeType)
  );
}

function resolveStreamKind(url, mimeType = "") {
  if (isFlvUrl(url) || isFlvMimeType(mimeType)) return "flv";
  if (isHlsUrl(url) || isHlsMimeType(mimeType)) return "hls";
  return "native";
}

export function extractVideoSrc(video) {
  for (const attr of VIDEO_SRC_ATTRS) {
    const value = video.getAttribute(attr)?.trim();
    if (value) return value;
  }

  for (const source of Array.from(video.querySelectorAll("source"))) {
    const src = source.getAttribute("src")?.trim();
    if (src) return src;
  }
  return "";
}

export function extractVideoMimeType(video) {
  const type = video.getAttribute("type")?.trim();
  if (type) return type;

  const source = video.querySelector("source");
  return source?.getAttribute("type")?.trim() ?? "";
}

function prepareLiveAutoplay(video) {
  video.muted = true;
  video.defaultMuted = true;
  video.setAttribute("muted", "");
  video.setAttribute("autoplay", "");
  video.setAttribute("playsinline", "");
  video.setAttribute("webkit-playsinline", "");
}

function tryAutoplayLiveVideo(video) {
  const startPlay = () => {
    video.play().catch((error) => {
      console.warn("[直播] 自动播放被浏览器拦截，请手动点击播放:", error);
    });
  };

  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    startPlay();
    return;
  }

  video.addEventListener("canplay", startPlay, { once: true });
}

function stripNativeStreamSrc(video) {
  video.removeAttribute("src");
  video.querySelectorAll("source").forEach((source) => source.remove());
  if (video.src) {
    video.removeAttribute("src");
    video.load();
  }
}

export function destroyHlsVideo(video) {
  const hls = hlsInstances.get(video);
  if (hls) {
    hls.destroy();
    hlsInstances.delete(video);
  }

  const flvPlayer = flvInstances.get(video);
  if (flvPlayer) {
    flvPlayer.pause();
    flvPlayer.unload();
    flvPlayer.detachMediaElement();
    flvPlayer.destroy();
    flvInstances.delete(video);
  }

  video.removeAttribute("src");
  video.load();
}

function bindHlsErrorRecovery(hls, video, url) {
  hls.on(Hls.Events.ERROR, (_event, data) => {
    console.error("[HLS] 播放错误:", url, data);
    if (!data.fatal) return;
    switch (data.type) {
      case Hls.ErrorTypes.NETWORK_ERROR:
        hls.startLoad();
        break;
      case Hls.ErrorTypes.MEDIA_ERROR:
        hls.recoverMediaError();
        break;
      default:
        hls.destroy();
        hlsInstances.delete(video);
        break;
    }
  });
}

function setupFlvVideo(video, url) {
  stripNativeStreamSrc(video);
  prepareLiveAutoplay(video);

  if (!flvjs.isSupported()) {
    video.src = url;
    tryAutoplayLiveVideo(video);
    return;
  }

  const flvPlayer = flvjs.createPlayer(
    {
      type: "flv",
      url,
      isLive: true,
      cors: true,
      withCredentials: false,
    },
    {
      enableStashBuffer: false,
      enableWorker: true,
    },
  );

  flvPlayer.attachMediaElement(video);
  flvPlayer.load();
  flvPlayer.on(flvjs.Events.METADATA_ARRIVED, () => {
    tryAutoplayLiveVideo(video);
  });
  flvInstances.set(video, flvPlayer);
}

function setupHlsStream(video, url) {
  stripNativeStreamSrc(video);
  prepareLiveAutoplay(video);

  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = url;
    tryAutoplayLiveVideo(video);
    return;
  }

  if (!Hls.isSupported()) {
    console.error("[HLS] 当前浏览器不支持 MSE，无法播放:", url);
    return;
  }

  const hls = new Hls({
    enableWorker: true,
    lowLatencyMode: false,
    liveBackBufferLength: 0,
    backBufferLength: 0,
    maxBufferLength: 30,
    maxMaxBufferLength: 600,
    manifestLoadingMaxRetry: 4,
    manifestLoadingRetryDelay: 1000,
    levelLoadingMaxRetry: 4,
    fragLoadingMaxRetry: 6,
    fragLoadingRetryDelay: 1000,
  });

  hlsInstances.set(video, hls);
  bindHlsErrorRecovery(hls, video, url);

  hls.attachMedia(video);
  hls.on(Hls.Events.MEDIA_ATTACHED, () => {
    stripNativeStreamSrc(video);
    hls.loadSource(url);
  });
  hls.on(Hls.Events.MANIFEST_PARSED, () => {
    tryAutoplayLiveVideo(video);
  });
}

export function setupHlsVideo(video, src, mimeType = "") {
  const url = src.trim();
  if (!url) return;

  destroyHlsVideo(video);

  const kind = resolveStreamKind(url, mimeType);
  if (kind === "flv") {
    setupFlvVideo(video, url);
    return;
  }
  if (kind === "hls") {
    setupHlsStream(video, url);
    return;
  }

  video.src = url;
}

export function processContentVideos(html) {
  if (!html) return html;

  const sanitized = html.replace(
    /(<video\b[^>]*?)\s+src=(["'])([^"']+)\2/gi,
    (match, prefix, quote, src) => {
      if (!isLiveVideoSource(src)) return match;
      return `${prefix} data-video-src=${quote}${src}${quote}`;
    },
  );

  if (typeof DOMParser === "undefined") return sanitized;

  const doc = new DOMParser().parseFromString(sanitized, "text/html");

  doc.querySelectorAll("video").forEach((video) => {
    const src = extractVideoSrc(video);
    const mimeType = extractVideoMimeType(video);
    const isStream = isLiveVideoSource(src, mimeType);

    video.classList.add("hls-video-player");
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    if (!video.hasAttribute("controls")) {
      video.setAttribute("controls", "");
    }
    if (src) {
      video.setAttribute("data-video-src", src);
    }
    if (mimeType) {
      video.setAttribute("data-video-type", mimeType);
    }

    if (isStream) {
      stripNativeStreamSrc(video);
      prepareLiveAutoplay(video);
    }
  });

  return doc.body.innerHTML;
}

export function initContentVideoPlayers(container) {
  const cleanups = [];

  container.querySelectorAll("video.hls-video-player").forEach((video) => {
    if (video.dataset.streamReady === "1") return;

    const mimeType =
      extractVideoMimeType(video) ||
      video.getAttribute("data-video-type")?.trim() ||
      "";
    const src =
      video.getAttribute("data-video-src")?.trim() || extractVideoSrc(video);
    if (!src) return;

    stripNativeStreamSrc(video);
    setupHlsVideo(video, src, mimeType);
    video.dataset.streamReady = "1";
    cleanups.push(() => {
      video.dataset.streamReady = "0";
      destroyHlsVideo(video);
    });
  });

  return () => {
    cleanups.forEach((cleanup) => cleanup());
  };
}
